import { CurrencyInput } from "@components/CurrencyInput";
import { useRtl } from "@hooks/useRtl";
import { StoreBadges } from "@components/shared/StoreBadges";
import { estimateUnlock } from "@/lib/financing";
import { UNLOCK } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { track } from "@services/analytics";
import { CalculatorUpdatedEvent } from "@services/analytics/types";
import { useTranslations } from "@services/strings";
import debounce from "just-debounce-it";
import { useId, useMemo, useRef, useState } from "react";

const TENORS_YEARS = [3, 5, 7, UNLOCK.maxYears] as const;

const PROPERTY = { min: 1_000_000, max: 100_000_000, step: 250_000 };
const DUES = { min: 0, max: 50_000_000, step: 250_000 };

type Tone = "white" | "mist";

const Slider = ({
  id,
  value,
  min,
  max,
  step,
  onChange,
  label,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  label: string;
}) => {
  // The slider runs the way the page reads: min at the start edge. The
  // numbers beside it stay Western digits either way.
  const rtl = useRtl();

  return (
    <input
      id={id}
      type="range"
      aria-label={label}
      min={min}
      max={max}
      step={step}
      value={Math.min(Math.max(value, min), max)}
      onChange={(e) => onChange(Number(e.target.value))}
      dir={rtl ? "rtl" : "ltr"}
      className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-ink [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-ink [&::-webkit-slider-thumb]:shadow"
    />
  );
};

const InputGroup = ({
  id,
  label,
  value,
  onChange,
  range,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  range: { min: number; max: number; step: number };
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-sm font-medium text-ink">
      {label}
    </label>
    <CurrencyInput
      id={id}
      value={value}
      onChange={onChange}
      className="tabular h-12 border-slate-200 bg-white text-base font-semibold"
    />
    <Slider
      id={`${id}-range`}
      label={label}
      value={value}
      onChange={onChange}
      {...range}
    />
  </div>
);

type Inputs = {
  propertyValue: number;
  hasDues: boolean;
  dues: number;
  years: number;
};

/**
 * The calculator events this estimator can emit: the vehicle one is the
 * auto-loan calculator's, and income is not asked for (see `lib/financing`).
 */
type EstimatorEvent = Exclude<
  CalculatorUpdatedEvent,
  "vehicle-value-changed" | "income-changed"
>;

type Change = (event: EstimatorEvent, value: number) => void;

/** The property value, the dues toggle and the tenor picker. */
const EstimatorInputs = ({
  inputs,
  onChange,
  onDuesToggle,
  tone,
}: {
  inputs: Inputs;
  onChange: Change;
  onDuesToggle: (hasDues: boolean) => void;
  tone: Tone;
}) => {
  const { t } = useTranslations();
  const uid = useId();

  return (
    <div
      className={cn(
        "flex flex-col gap-7 rounded-3xl border border-slate-200 p-6 md:p-8",
        tone === "mist" ? "bg-white" : "bg-mist",
      )}
    >
      <InputGroup
        id={`${uid}-property`}
        label={t("site.estimator.propertyValue")}
        value={inputs.propertyValue}
        range={PROPERTY}
        onChange={(value) => onChange("property-value-changed", value)}
      />
      <div className="flex flex-col gap-4">
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 accent-navy"
            checked={inputs.hasDues}
            onChange={(e) => onDuesToggle(e.target.checked)}
          />
          {t("site.estimator.duesToggle")}
        </label>
        {inputs.hasDues ? (
          <InputGroup
            id={`${uid}-dues`}
            label={t("site.estimator.dues")}
            value={inputs.dues}
            range={DUES}
            onChange={(value) => onChange("dues-changed", value)}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">
          {t("site.estimator.tenor")}
        </span>
        <div className="grid grid-cols-4 gap-2" role="group">
          {TENORS_YEARS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={inputs.years === option}
              onClick={() => onChange("tenor-changed", option)}
              className={cn(
                "h-11 rounded-xl border text-sm font-semibold transition-colors",
                inputs.years === option
                  ? "border-ink bg-ink text-white"
                  : "border-slate-200 bg-white text-ink hover:border-slate-400",
              )}
            >
              {t("site.common.yearsPlural", { count: option })}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

type Estimate = ReturnType<typeof estimateUnlock>;

/** Why the number is what it is: the rule that set it. */
const useExplanation = (estimate: Estimate, hasDues: boolean): string => {
  const { t, formatNumber, formatPercent } = useTranslations();

  if (estimate.limitedBy === "minimum") {
    return t("site.estimator.belowMinimum", {
      minAmount: formatNumber(UNLOCK.minAmount),
    });
  }
  if (estimate.limitedBy === "maximum") {
    return t("site.estimator.capped", {
      maxAmount: formatNumber(UNLOCK.maxAmount),
    });
  }
  return t(hasDues ? "site.estimator.ltvAfterDues" : "site.estimator.ltv", {
    ltv: formatPercent(UNLOCK.maxLoanToValue),
  });
};

/** The navy result panel: the cash, its monthly payment, and the rule that set it. */
const EstimatorResult = ({
  estimate,
  hasDues,
}: {
  estimate: Estimate;
  hasDues: boolean;
}) => {
  const { t, formatNumber, formatCurrency } = useTranslations();
  const amount = estimate.cash;
  const monthly = Math.round(estimate.monthly / 100) * 100;
  const explanation = useExplanation(estimate, hasDues);

  return (
    <div className="flex flex-col justify-between rounded-3xl bg-navy p-6 text-white md:p-8">
      <div>
        <p className="text-sm text-slate-300">{t("site.estimator.result")}</p>
        <p
          className="tabular mt-2 flex flex-wrap items-baseline gap-2"
          aria-live="polite"
        >
          <span className="text-base font-medium text-slate-300">EGP</span>
          <span className="display text-4xl md:text-5xl">
            {formatNumber(amount)}
          </span>
        </p>
        {amount > 0 ? (
          <p className="mt-4 text-body text-slate-200">
            {t("site.estimator.monthlyOnAmount")}:{" "}
            <span className="tabular font-semibold text-white">
              {formatCurrency(monthly)}
            </span>{" "}
            <span className="text-slate-400">{t("site.common.perMonth")}</span>
          </p>
        ) : null}
        <p className="mt-3 text-sm text-slate-300">{explanation}</p>
      </div>

      <div className="mt-8 border-t border-white/10 pt-6">
        <p className="text-sm font-semibold">{t("site.estimator.nextStep")}</p>
        <StoreBadges placement="estimator" className="mt-3" />
      </div>
    </div>
  );
};

/** Which input each calculator event changes. */
const INPUT_FOR_EVENT: Record<EstimatorEvent, keyof Inputs> = {
  "property-value-changed": "propertyValue",
  "dues-changed": "dues",
  "tenor-changed": "years",
};

/**
 * "What could you unlock?" — the LTV and amortisation rules the app applies,
 * on the property value alone. The result panel says which rule set the
 * number so people know what would move it.
 */
export const Estimator = ({ tone = "white" }: { tone?: Tone }) => {
  const { t } = useTranslations();
  const [inputs, setInputs] = useState<Inputs>({
    propertyValue: 5_000_000,
    hasDues: false,
    dues: 1_000_000,
    years: UNLOCK.maxYears,
  });

  const trackChange = useRef(
    debounce((event: CalculatorUpdatedEvent, value: number) => {
      track({ event: "Calculator Updated", properties: { event, value } });
    }, 1500),
  );

  const onChange: Change = (event, value) => {
    setInputs((current) => ({ ...current, [INPUT_FOR_EVENT[event]]: value }));
    trackChange.current(event, value);
  };

  const estimate = useMemo(
    () =>
      estimateUnlock({
        propertyValue: inputs.propertyValue,
        outstandingDues: inputs.hasDues ? inputs.dues : 0,
        annualInterestRate: UNLOCK.indicativeAnnualRate,
        termMonths: inputs.years * 12,
        maxLoanToValue: UNLOCK.maxLoanToValue,
        maxAmount: UNLOCK.maxAmount,
        minAmount: UNLOCK.minAmount,
      }),
    [inputs],
  );

  return (
    <section
      id="estimator"
      className={cn(
        "scroll-mt-20 py-16 md:py-28",
        tone === "mist" ? "bg-mist" : "bg-white",
      )}
    >
      <div className="container">
        <div className="max-w-prose">
          <h2 className="display text-h2-sm md:text-h2">
            {t("site.estimator.title")}
          </h2>
          <p className="mt-4 text-body text-slate-600">
            {t("site.estimator.intro")}
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-10">
          <EstimatorInputs
            inputs={inputs}
            onChange={onChange}
            onDuesToggle={(hasDues) =>
              setInputs((current) => ({ ...current, hasDues }))
            }
            tone={tone}
          />
          <EstimatorResult estimate={estimate} hasDues={inputs.hasDues} />
        </div>
      </div>
    </section>
  );
};
